import type { Badge, FishFinderState, VisitedLocation } from "./types";

// Auto-badges at 25/50/75/100% of a location's likely-species list.
export const BADGE_THRESHOLDS = [25, 50, 75, 100] as const;

export function locationCompletion(
  loc: VisitedLocation,
  seenTaxonIds: Set<number>,
): { seen: number; total: number; pct: number } {
  const total = loc.cached_species.length;
  if (total === 0) return { seen: 0, total: 0, pct: 0 };
  let seen = 0;
  for (const s of loc.cached_species) {
    if (seenTaxonIds.has(s.taxon_id)) seen += 1;
  }
  return { seen, total, pct: Math.floor((seen / total) * 100) };
}

// Recompute badges across every cached location. Returns the next badge list
// plus any badges newly unlocked by the most recent change (for a toast).
export function syncBadges(state: FishFinderState): {
  badges: Badge[];
  unlocked: Badge[];
} {
  const seenTaxonIds = new Set(state.lifelist.map((e) => e.taxon_id));
  const existing = new Set(
    state.badges.map((b) => `${b.location_key}:${b.threshold}`),
  );
  const badges = [...state.badges];
  const unlocked: Badge[] = [];

  for (const loc of state.visited_locations) {
    const { pct } = locationCompletion(loc, seenTaxonIds);
    for (const threshold of BADGE_THRESHOLDS) {
      if (pct < threshold) continue;
      const id = `${loc.key}:${threshold}`;
      if (existing.has(id)) continue;
      const badge: Badge = {
        location_key: loc.key,
        location_name: loc.name,
        threshold,
        unlocked_at: new Date().toISOString(),
      };
      badges.push(badge);
      unlocked.push(badge);
      existing.add(id);
    }
  }

  return { badges, unlocked };
}
